/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';

/**
 * Defines the prop type definitions for the WidgetTitle component, which are
 * as follows:
 *
 * title     : This is the required string property which is the title of the widget.
 * info      : This is the optional string property which is the information of the widget.
 * className : This is the optional string property which is the css class name if need to
 *             apply to the title.
 */
export interface WidgetTitleProps {
  title: string;
  info?: string;
  className?: string;
};

export const useStyles = makeStyles(() => ({
  widgetTtile : {
    alignSelf  : 'flex-start',
    marginLeft : '12px'
  },
  infoIcon : {
    margin        : '0 6px',
    display       : 'inline-block',
    verticalAlign : 'middle',
    cursor        : 'pointer'
  }
}));

/**
 * Definition of the WidgetTitle component. This is the reusable component when the
 * widgets  have the title in their's header along with the additional information
 * if any on the info icon.
 *
 * ```typescript
 *
 *  import WidgetTitle from './WidgetTitle';
 *
 *  <WidgetTitle title={'Fleet Status'} /> // just with title
 *  <WidgetTitle title={'Fleet Status'}
 *    info={'The current status of all fleets'} /> // title along with the tooltip
 *  <WidgetTitle title={'Fleet Status'}
 *    className={'widget-title'} /> // css change if need
 *
 * ```
 */
export const WidgetTitle = ({title, info, className}: WidgetTitleProps) => {
  const wrapperClasses = useStyles();
  return (
    <LabelTypography variantType='label1' className={className ?? wrapperClasses.widgetTtile}>
      {title}
      {!!info && (
        <Tooltip arrow
          title={info}
          placement={'bottom-start'}>
          <span className={wrapperClasses.infoIcon}>
            <InfoIcon size={18 as any} stroke={dlsColor('grey3')} />
          </span>
        </Tooltip>
      )}
    </LabelTypography>
  );
};

export default WidgetTitle;
